/**
 * Sidebar navigation config — Phase 1.10 + Phase 7 overview alignment (2026-04-22).
 *
 * Domain layout (location-scope rail only — company-scope uses CompanySidebar):
 *
 *   Frontdesk                /bookings, /guests, /rooms, /activities, /persons
 *   Commerce                 /pos, /sales, /bar-kitchen, /shop, /suppliers
 *   Team                     /staff, /shifts, /payroll, /staff/tokens
 *   Treasury                 /treasury/*
 *   -------- (rail-foot) ---
 *   Settings                 /settings, /settings/integrations
 *
 * Phase 7 (2026-04-22): the Location cross-domain umbrella domain was retired
 * from the rail because it duplicated signals already covered by the four
 * editorial overview pages (Frontdesk / Commerce / Team / Treasury) and its
 * "location-specific day" framing was not the canonical entry point. Per-
 * location context still flows through the active-location switcher; each
 * domain overview is already scoped to the active location.
 *
 * Dropped from the old layout: the "Personal" group (profile + notifications
 * moved to the UserMenu), and the "Admin" group (its Settings + Integrations
 * items live in the rail-foot, and "Management overview" is the landing domain).
 * Tokens moved from Finance to Team because it is a staff-linked ledger.
 */

import type { ComponentType } from 'react';
import type { TFunction } from 'i18next';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  UsersRound,
  Bed,
  Waves,
  ConciergeBell,
  Monitor,
  BarChart3,
  UtensilsCrossed,
  Store,
  Truck,
  ShoppingBag,
  UserCog,
  Briefcase,
  CalendarClock,
  Wallet as WalletIcon,
  Coins,
  Landmark,
  ReceiptText,
  HandCoins,
  Sparkles,
  Building2,
  CalendarRange,
  FileText,
  Phone,
  Settings,
  Plug,
  ClipboardList,
  Gauge,
} from 'lucide-react';

/**
 * The "management" domain was retired from the location-scope rail on
 * 2026-04-25 — its only item (`/location/management/overview`) was actually a
 * cross-location company-scope surface mounted at the wrong place.
 * It now lives at `/company/overview` and is reached via the company
 * sidebar (CompanySidebar). The location rail no longer carries any
 * cross-location entry point.
 */
export type DomainId =
  // Location-scope domains
  | 'frontdesk'
  | 'commerce'
  | 'team'
  | 'treasury'
  | 'management'
  | 'settings'
  // Company-scope domains (consumed by companyNavConfig + CompanyRailSidebar)
  | 'operation'
  | 'people'
  | 'finance'
  | 'admin';

/**
 * Keys that drive per-item / per-domain gate evaluation.
 * The values are resolved at render time from the user's RBAC + active modules.
 */
export interface GateFlags {
  canSeeBookings: boolean;
  canSeeGuests: boolean;
  canSeeAccommodations: boolean;
  canSeeActivities: boolean;
  canSeePOS: boolean;
  /** Sales is readable without operating the till — see canViewPOSSales. */
  canSeeSales: boolean;
  canSeeInventory: boolean;
  canSeeStaff: boolean;
  canSeeShifts: boolean;
  canSeeSuppliers: boolean;
  canSeePayroll: boolean;
  canSeeTreasury: boolean;
  canSeePlatformInvoices: boolean;
  canManageTreasuryBanking: boolean;
  canManageTreasuryReconciliation: boolean;
  /** Gates the "Users & Roles" entry inside the location Management panel. */
  canManageUsers: boolean;
  /** Gates the Management "Overview" entry (income/staff/billing KPIs). */
  /** Configure THIS location — Management Overview + the Settings domain.
   *  `company.manage_locations` OR `location.manage_settings` on this site. */
  canManageLocationSettings: boolean;
  modulesActivities: boolean;
  modulesPos: boolean;
  modulesInventory: boolean;
  modulesTreasury: boolean;
  modulesHr: boolean;
}

export interface SubItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * Items rendered inside a domain's panel.
 *
 *  - `kind: 'section'` → non-clickable header with a small uppercase mono
 *    label. Used by the Settings panel to group its 11 items into 4
 *    conceptual buckets without nesting / collapsibles.
 *  - `kind` omitted (default) → the regular clickable entry, with an icon
 *    + optional URL + optional sub-items.
 *
 * `icon` is only required for clickable entries; section rows have no
 * icon. Caller-side type-checking is light because the rail UI handles
 * both branches.
 */
export interface PanelItem {
  /** Discriminator. Defaults to a clickable entry when omitted. */
  kind?: 'section';
  title: string;
  url?: string;
  icon?: ComponentType<{ className?: string }>;
  subItems?: SubItem[];
  gate?: boolean;
  /**
   * Extra URL prefixes that should keep this rail item highlighted even
   * when the current pathname doesn't start with `url`. Used by surfaces
   * whose in-page tabs navigate to sibling routes (e.g. Treasury
   * Operations covers `/treasury/operations`, `/treasury/pending`, and
   * `/treasury/cash` — the rail should stay highlighted on all three).
   */
  matchPrefixes?: string[];
  /**
   * When set, the item's own `url` is matched EXACTLY (no prefix match), so a
   * nested sibling route doesn't light it up. Needed for `People` (/staff),
   * whose URL is a prefix of the unrelated `Tokens` route (/staff/tokens):
   * without this, opening Tokens highlighted both. `matchPrefixes` still
   * applies, so intended children (e.g. /staff/positions) stay highlighted.
   */
  exact?: boolean;
}

export interface Domain {
  id: DomainId;
  label: string;
  monogram: string;
  icon: ComponentType<{ className?: string }>;
  /** Where the rail button navigates when clicked. Defaults to first item URL. */
  hubUrl: string;
  /** Matching URL prefixes to decide the active domain from location.pathname. */
  matchPrefixes: string[];
  items: PanelItem[];
  /** If false, the rail hides this domain entirely. */
  visible: boolean;
  /** If true, render a visual separator after this domain in the rail. */
  separated?: boolean;
}

interface BuildParams {
  // Accept the full i18next TFunction so callers can pass the value straight
  // from `useTranslation()` — previously we narrowed it to (key, fallback)
  // which stopped type-checking once react-i18next aligned with modern TFunction.
  t: TFunction;
  gates: GateFlags;
  /**
   * Active location id from the URL. When provided, the nav config
   * expands `/location/*` URLs into `/location/:id/*` so links and
   * matchPrefixes match the scope-in-URL routing shape (see
   * docs/plan/SCOPE_IN_URL.md). When omitted, URLs stay legacy.
   *
   * During Step 2 (Treasury piloto), only Treasury routes are
   * migrated server-side; the helper is still applied to every link
   * because legacy routes have a redirect wrapper that re-injects the
   * last-seen id.
   */
  locationId?: string | null;
}

/**
 * Build the domain tree. Items inside a domain are filtered by gate; a domain
 * with no visible items is hidden from the rail (visible=false).
 */
export function buildDomains({ t, gates, locationId }: BuildParams): Domain[] {
  /**
   * Rewrite a static `/location/...` URL into the path-scoped shape
   * `/location/{locationId}/...` when an id is available. No-op for
   * any other prefix (`/company/...`, `/persons/...`, etc.).
   */
  const loc = (url: string): string => {
    if (!locationId) return url;
    if (!url.startsWith('/location/')) return url;
    // Avoid double-injection if the caller already wrote the new shape.
    const rest = url.slice('/location/'.length);
    const firstSegment = rest.split('/')[0];
    if (firstSegment === locationId) return url;
    return `/location/${locationId}/${rest}`;
  };

  // Domain matchPrefixes must cover BOTH the scope-in-URL shape
  // (`/location/{id}/...`, what every location route resolves to today)
  // AND the legacy `/location/...` shape the redirect wrapper can briefly
  // land on. Without the scoped form `resolveDomainFromPath` matches
  // nothing and the rail panel stays stuck on the fallback domain.
  const mp = (prefixes: string[]): string[] => {
    const out: string[] = [];
    for (const p of prefixes) {
      const scoped = loc(p);
      if (scoped !== p) out.push(scoped);
      out.push(p);
    }
    return out;
  };

  const {
    modulesActivities,
    modulesPos,
    modulesInventory,
    modulesTreasury,
    modulesHr,
  } = gates;

  // Frontdesk rail flattened on 2026-04-25 — sub-menus removed because each
  // module page now carries its own in-page tab layout that absorbs the old
  // sub-items (Bookings has Today/Upcoming/Calendar/All/Walk-ins; Guests will
  // get Guests/Offerings tabs; Activities will get Templates/Calendar/
  // Registrations tabs). Check-in/out is a CTA inside BookingsPage's chip-row,
  // not a tab — it's an action surface, not a view. Persons (Directory)
  // remoted to ⌘K + cross-scope ContextualPage; not a Frontdesk leaf anymore.
  const frontdesk: PanelItem[] = [
    {
      title: t('nav.frontdeskOverview', 'Overview'),
      icon: LayoutDashboard,
      url: loc('/location/frontdesk/overview'),
      // Same fix as Commerce / Team Overview: require an actual Frontdesk
      // permission, not merely a location session. Without a gate this item
      // was always present, so the whole Frontdesk domain (visible ===
      // items.length > 0) leaked into the rail for HR / Commerce / Treasury
      // roles as a bogus "home" they can't operate.
      gate:
        gates.canSeeBookings
        || gates.canSeeGuests
        || gates.canSeeAccommodations
        || (gates.canSeeActivities && modulesActivities),
    },
    {
      title: t('nav.frontdeskBookings', 'Bookings'),
      icon: BookOpen,
      url: loc('/location/bookings'),
      gate: gates.canSeeBookings,
    },
    {
      title: t('nav.frontdeskGuests', 'Guests'),
      icon: Users,
      url: loc('/location/guests'),
      gate: gates.canSeeGuests,
    },
    {
      title: t('nav.frontdeskRooms', 'Rooms'),
      icon: Bed,
      url: loc('/location/rooms'),
      gate: gates.canSeeAccommodations,
    },
    {
      title: t('nav.frontdeskActivities', 'Activities'),
      icon: Waves,
      url: loc('/location/activities'),
      gate: gates.canSeeActivities && modulesActivities,
    },
    {
      // Services sits under Activities as a sibling. It reuses the Activities
      // gate (any activities permission + 'activities' module) by design — no separate
      // Services permission/module for now. The page owns its own tabs; the
      // Providers tab (which reads the suppliers API) is hidden inside the page
      // for users without supplier access, so the nav entry stays activities-gated.
      title: t('nav.frontdeskServices', 'Services'),
      icon: ConciergeBell,
      url: loc('/location/services'),
      gate: gates.canSeeActivities && modulesActivities,
    },
  ];

  // Commerce rail
  //
  // Overview is the first rail item — keeps Commerce visually consistent
  // with Frontdesk / Team / Treasury, which all expose Overview as the
  // first sub-menu entry. The page itself aggregates KPIs across the
  // five sub-domains (Bar & Kitchen, Shop, POS, Sales, Suppliers).
  //
  // Operational order after Overview is:
  //   Bar & Kitchen → Shop → POS → Sales → Suppliers
  //
  // Bar & Kitchen lands on the "Today" tab (current state — open
  // orders, kitchen queue, today's revenue, top items). Shop also
  // lands on its own "Today" tab. POS lands on the admin tab strip
  // (Terminals · Daily Close); the workstation itself is reached via
  // the centered POS launcher in the app header (gated by
  // canUseWebPOS + a web-enabled terminal). Suppliers gains a tab
  // strip — Suppliers · Purchase Orders · History — and uses a Truck
  // icon to avoid the Store-vs-Shop visual conflict.
  //
  // Rail URLs use the domain *prefix*, not the default-tab path. The
  // sidebar's `isUrlActive` check treats anything under `/location/
  // bar-kitchen/*` as "Bar & Kitchen is the current rail item", so
  // navigating to /menus or /products keeps the rail item highlighted.
  // The default-tab redirect routes in App.tsx handle navigation:
  // clicking the rail goes to /location/bar-kitchen which immediately
  // redirects to /today (or /terminals for POS).
  const commerce: PanelItem[] = [
    {
      title: t('nav.commerceOverview', 'Overview'),
      icon: LayoutDashboard,
      url: loc('/location/products-sales/overview'),
      // Commerce spans POS + Inventory. Show the domain overview only when
      // the user actually has a Commerce permission for the active module —
      // not merely because the module is enabled on the location. Without
      // the RBAC half a Frontdesk-only user saw Commerce leak into the rail.
      // Boolean() is load-bearing: pruneItems filters on `gate !== false`,
      // so a short-circuit yielding undefined would leak the item through.
      gate: Boolean(((gates.canSeePOS || gates.canSeeSales) && modulesPos)
        || (gates.canSeeInventory && modulesInventory)),
    },
    {
      title: t('nav.barKitchen', 'Bar & Kitchen'),
      icon: UtensilsCrossed,
      url: loc('/location/bar-kitchen'),
      gate: gates.canSeeInventory && modulesInventory,
    },
    {
      title: t('nav.shop', 'Retail'),
      icon: Store,
      url: loc('/location/shop'),
      gate: gates.canSeeInventory && modulesInventory,
    },
    {
      title: t('nav.pointOfSale', 'Point of Sale'),
      icon: Monitor,
      url: loc('/location/pos'),
      gate: gates.canSeePOS && modulesPos,
    },
    {
      title: t('nav.sales', 'Sales'),
      icon: BarChart3,
      url: loc('/location/sales'),
      gate: gates.canSeeSales && modulesPos,
    },
    {
      title: t('nav.suppliers', 'Suppliers'),
      icon: Truck,
      url: loc('/location/suppliers'),
      gate: gates.canSeeSuppliers && modulesInventory,
    },
  ];

  const team: PanelItem[] = [
    {
      title: t('nav.teamOverview', 'Overview'),
      icon: LayoutDashboard,
      url: loc('/location/hr/overview'),
      // Same fix as Commerce Overview: require an actual Team permission,
      // not just the HR module being enabled, so the domain doesn't leak
      // into the rail for users without any staff/shifts/payroll access.
      gate: (gates.canSeeStaff || gates.canSeeShifts || gates.canSeePayroll) && modulesHr,
    },
    // Team rail flattened on 2026-04-26 — People sub-menu retired. The
    // /location/staff page now shares an Employees / Positions tab strip
    // with /location/staff/positions, same pattern as Bookings/Guests/etc.
    {
      title: t('nav.hrPeople', 'People'),
      icon: UserCog,
      url: loc('/location/staff'),
      // Exact match: /staff/tokens is a sibling rail item nested under this
      // URL, so a prefix match would light up both. Positions is the one
      // intended child (Employees/Positions tab strip) — keep it highlighted.
      exact: true,
      matchPrefixes: [loc('/location/staff/positions')],
      gate: gates.canSeeStaff && modulesHr,
    },
    {
      title: t('nav.hrShiftsCalendar', 'Shifts'),
      icon: CalendarClock,
      url: loc('/location/shifts'),
      gate: gates.canSeeShifts && modulesHr,
    },
    {
      title: t('nav.hrPayroll', 'Payroll'),
      icon: WalletIcon,
      url: loc('/location/payroll'),
      gate: gates.canSeePayroll && modulesHr,
    },
    {
      title: t('nav.tokens', 'Tokens'),
      icon: Coins,
      url: loc('/location/staff/tokens'),
      gate: gates.canSeeStaff && modulesHr,
    },
  ];

  // Treasury rail collapsed on 2026-04-26 from 7 → 4 items. Pending,
  // Cash, and Reconciliation are all operational sub-views of the same
  // working flow (an obligation moves from pending → settled-via-cash or
  // settled-via-reconciled-bank-tx); they now share an Operations tab
  // strip with the operations page itself. Overview, Banking and Reports
  // serve different audiences (dashboard / config / reporting) so they
  // stay top-level.
  const treasury: PanelItem[] = [
    {
      title: t('nav.treasuryOverview', 'Overview'),
      icon: LayoutDashboard,
      url: loc('/location/treasury/overview'),
      gate: gates.canSeeTreasury && modulesTreasury,
    },
    // Banking — read-only operational visualization (balances, last
    // sync, recent movements). Setup of banks (add/link/configure)
    // stays under Settings → Treasury; this rail item is for the
    // operator's daily-state view, not for configuration.
    {
      title: t('nav.treasuryBanking', 'Banking'),
      icon: Landmark,
      url: loc('/location/treasury/banking'),
      gate: gates.canSeeTreasury && modulesTreasury,
    },
    {
      title: t('nav.treasuryOperationsHub', 'Operations'),
      icon: ReceiptText,
      url: loc('/location/treasury/operations'),
      // Pending and Cash are now in-page tabs of Operations
      // (`?tab=pending`, `?tab=cash`). The standalone routes stay
      // mounted for bookmark compatibility; matchPrefixes keeps the
      // rail entry highlighted while those URLs are still landed on.
      matchPrefixes: [
        loc('/location/treasury/pending'),
        loc('/location/treasury/cash'),
      ],
      gate: gates.canSeeTreasury && modulesTreasury,
    },
    {
      title: t('nav.treasuryReports', 'Reports'),
      icon: BarChart3,
      url: loc('/location/treasury/reports'),
      gate: gates.canSeeTreasury && modulesTreasury,
    },
  ];

  // Management — location-scope admin entry point (re-added 2026-05-30
  // after being retired for 5 weeks). Different intent than the original
  // cross-location surface that lived here: this one is scoped to THIS
  // location (Overview is a per-location dashboard, Users & Roles is
  // filtered to UserAccesses against this location). Visually the rail
  // separates it from the operational domains above via `separated: true`
  // on the previous domain (treasury), so Management reads as the admin
  // cluster while Settings stays in its own dedicated bottom slot.
  const management: PanelItem[] = [
    {
      title: t('nav.managementOverview', 'Overview'),
      icon: LayoutDashboard,
      url: loc('/location/management/overview'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.managementUsersPermissions', 'Users & Roles'),
      icon: UserCog,
      url: loc('/location/users'),
      gate: gates.canManageUsers,
    },
    {
      // What Shaka bills for THIS location. Sits under Management rather
      // than Treasury so it never reads as the location's own money, and
      // it is hidden unless the owner granted location.view_invoices.
      title: t('platformInvoices.title', 'Platform invoices'),
      icon: FileText,
      url: loc('/location/invoices'),
      gate: gates.canSeePlatformInvoices,
    },
  ];

  // Settings — 11 items grouped into 4 conceptual buckets via inline
  // section headers (Option A from the rail-grouping audit, 2026-04-28).
  // The legacy LocationSettingsPage used a vertical tab strip; the
  // redesign first flattened those tabs into one rail entry per section,
  // and now layers subtle groups on top so the panel scans cleanly.
  // Routes already exist (/location/settings/*) — sections are pure UI.
  // The whole Settings domain is a manager surface, gated on
  // `canManageLocationSettings` — the company codename OR
  // `location.manage_settings` on this site, so the person who runs the
  // camp configures the camp. Operational roles (e.g. frontdesk) never see
  // the ST domain; `pruneItems` drops the now-orphaned section headers and
  // the domain auto-hides via `visible: settingsItems.length > 0`.
  const settings: PanelItem[] = [
    // ── Identity & space ─────────────────────────────────────────────
    { kind: 'section', title: t('nav.settingsSection.identity', 'Identity & space') },
    {
      title: t('nav.settingsGeneral', 'General'),
      icon: Building2,
      url: loc('/location/settings/general'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsContact', 'Contact'),
      icon: Phone,
      url: loc('/location/settings/contact'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsOperatingHours', 'Operating hours'),
      icon: CalendarClock,
      url: loc('/location/settings/operating-hours'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsSeasons', 'Seasons'),
      icon: CalendarRange,
      url: loc('/location/settings/seasons'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsCapacityLimits', 'Capacity & limits'),
      icon: Gauge,
      url: loc('/location/settings/capacity-limits'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsGuestForm', 'Guest form'),
      icon: ClipboardList,
      url: loc('/location/settings/guest-form'),
      gate: gates.canManageLocationSettings,
    },

    // ── Money & payments ─────────────────────────────────────────────
    { kind: 'section', title: t('nav.settingsSection.money', 'Money & payments') },
    {
      title: t('nav.settingsMonetary', 'Monetary defaults'),
      icon: Coins,
      url: loc('/location/settings/monetary'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsGuestPayment', 'Guest payment defaults'),
      icon: HandCoins,
      url: loc('/location/settings/guest-payment'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsBilling', 'Billing'),
      icon: FileText,
      url: loc('/location/settings/billing'),
      gate: gates.canManageLocationSettings,
    },
    {
      title: t('nav.settingsTreasury', 'Treasury'),
      icon: Landmark,
      url: loc('/location/settings/treasury'),
      gate: gates.canManageLocationSettings && gates.canSeeTreasury && modulesTreasury,
    },

    // ── People ───────────────────────────────────────────────────────
    { kind: 'section', title: t('nav.settingsSection.people', 'People') },
    {
      title: t('nav.settingsStaff', 'Staff tokens'),
      icon: Users,
      url: loc('/location/settings/staff'),
      gate: gates.canManageLocationSettings,
    },

    // ── Connections ──────────────────────────────────────────────────
    { kind: 'section', title: t('nav.settingsSection.connections', 'Connections') },
    {
      title: t('nav.integrations', 'Integrations'),
      icon: Plug,
      url: loc('/location/settings/integrations'),
      gate: gates.canManageLocationSettings,
    },
  ];

  const pruneItems = (items: PanelItem[]): PanelItem[] => {
    const pruned = items
      .filter((item) => item.kind === 'section' || item.gate !== false)
      .map((item) => {
        if (item.kind === 'section') return item;
        if (item.subItems) {
          // Sub-items inherit the parent's gate; prune to remove any explicitly
          // gated sub-items (none today, but future-proof).
          const visibleSubs = item.subItems;
          if (visibleSubs.length === 0) return null;
          return { ...item, subItems: visibleSubs };
        }
        return item;
      })
      .filter((item): item is PanelItem => Boolean(item));

    // Drop trailing or leading section headers and any section that ends
    // up adjacent to another (e.g. when every entry under it was gated
    // away by the user's permissions).
    return pruned.filter((item, idx, arr) => {
      if (item.kind !== 'section') return true;
      const next = arr[idx + 1];
      // Remove sections with no clickable entry following them.
      return Boolean(next) && next.kind !== 'section';
    });
  };

  const frontdeskItems = pruneItems(frontdesk);
  const commerceItems = pruneItems(commerce);
  const teamItems = pruneItems(team);
  const treasuryItems = pruneItems(treasury);
  const managementItems = pruneItems(management);
  const settingsItems = pruneItems(settings);

  const domains: Domain[] = [
    {
      id: 'management',
      label: t('nav.domains.management', 'Management'),
      monogram: 'MG',
      icon: Briefcase,
      // Derive the hub from what survived the gates, exactly as treasury
      // and settings do below. Hard-coding Overview here sent every
      // location-scope Manager — who sees the domain because Users & Roles
      // and Platform invoices pass — straight into the one entry they
      // could not open, i.e. a 403 on clicking Management (PENDINGS #49a).
      hubUrl: managementItems[0]?.url ?? loc('/location/management/overview'),
      matchPrefixes: mp(['/location/management', '/location/users']),
      items: managementItems,
      visible: managementItems.length > 0,
      // Management is the admin cluster — render it first in the rail
      // and draw a divider after it so the operational domains below
      // (frontdesk / commerce / team / treasury) read as the
      // day-to-day workspace, separate from the administration entry.
      separated: managementItems.length > 0,
    },
    {
      id: 'frontdesk',
      label: t('nav.domains.frontdesk', 'Frontdesk'),
      monogram: 'FD',
      icon: Bed,
      hubUrl: '/location/frontdesk/overview',
      matchPrefixes: mp(['/location/frontdesk', '/location/bookings', '/location/guests', '/persons', '/location/rooms', '/location/activities', '/location/services']),
      items: frontdeskItems,
      visible: frontdeskItems.length > 0,
    },
    {
      id: 'commerce',
      label: t('nav.domains.commerce', 'Commerce'),
      monogram: 'CM',
      icon: ShoppingBag,
      hubUrl: '/location/products-sales/overview',
      matchPrefixes: mp(['/location/products-sales', '/location/pos', '/location/sales', '/location/bar-kitchen', '/location/shop', '/location/suppliers']),
      items: commerceItems,
      visible: commerceItems.length > 0,
    },
    {
      id: 'team',
      label: t('nav.domains.team', 'Team'),
      monogram: 'TM',
      icon: UsersRound,
      hubUrl: '/location/hr/overview',
      matchPrefixes: mp(['/location/hr', '/location/staff', '/location/shifts', '/location/payroll']),
      items: teamItems,
      visible: teamItems.length > 0,
    },
    {
      id: 'treasury',
      label: t('nav.domains.treasury', 'Treasury'),
      monogram: 'TR',
      icon: Sparkles,
      hubUrl: treasuryItems[0]?.url ?? loc('/location/treasury/overview'),
      // Match both legacy and path-scoped Treasury URLs so the domain
      // stays highlighted while the redirect wrapper rewrites the URL.
      matchPrefixes: mp(['/location/treasury']),
      items: treasuryItems,
      visible: treasuryItems.length > 0,
    },
    {
      id: 'settings',
      label: t('nav.domains.settings', 'Settings'),
      monogram: 'ST',
      icon: Settings,
      hubUrl: settingsItems[0]?.url ?? '/location/settings',
      matchPrefixes: mp(['/location/settings']),
      items: settingsItems,
      visible: settingsItems.length > 0,
    },
  ];

  return domains;
}

/**
 * Determine which domain owns the given pathname.
 * Falls back to 'frontdesk' when no prefix matches — frontdesk is the
 * canonical entry point for a location-scoped session. (The location rail
 * does carry a 'management' domain, re-added 2026-05-30, but it is not the
 * fallback target.)
 */
export function resolveDomainFromPath(
  pathname: string,
  domains: Domain[],
  fallback: DomainId = 'frontdesk',
): DomainId {
  for (const domain of domains) {
    if (domain.matchPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return domain.id;
    }
  }
  return fallback;
}
